// Tiny fetch helper.

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const body = (await res.json()) as { error?: string; details?: unknown };
      if (body?.error) msg = body.error;
      if (body?.details) msg += ` (${JSON.stringify(body.details)})`;
    } catch {}
    throw new Error(msg);
  }
  return (await res.json()) as T;
}

export const api = {
  async submitVisit(body: unknown): Promise<{ ok: boolean; id: number }> {
    const res = await fetch("/api/visits/submit", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    return handle(res);
  },
  async adminLogin(email: string): Promise<{ ok: boolean }> {
    const res = await fetch("/api/admin/login", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email }),
    });
    return handle(res);
  },
  async adminLoginPassword(email: string, password: string): Promise<{ ok: boolean; email: string }> {
    const res = await fetch("/api/admin/login-password", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    return handle(res);
  },
  async listAdminUsers(): Promise<{ users: AdminUser[] }> {
    const res = await fetch("/api/admin/users");
    return handle(res);
  },
  async createAdminUser(email: string): Promise<{ ok: boolean; id: number }> {
    const res = await fetch("/api/admin/users", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email }),
    });
    return handle(res);
  },
  async setAdminPassword(id: number, password: string): Promise<{ ok: boolean }> {
    const res = await fetch(`/api/admin/users/${id}/password`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ password }),
    });
    return handle(res);
  },
  async setAdminActive(id: number, active: boolean): Promise<{ ok: boolean }> {
    const res = await fetch(`/api/admin/users/${id}/active`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ active }),
    });
    return handle(res);
  },
  async adminMe(): Promise<{ email: string; expiresAt: number }> {
    const res = await fetch("/api/admin/me");
    return handle(res);
  },
  async adminLogout(): Promise<void> {
    await fetch("/api/admin/logout", { method: "POST" });
  },
  async listVisits(params: Record<string, string>) {
    const q = new URLSearchParams(params).toString();
    const res = await fetch(`/api/admin/visits?${q}`);
    return handle<{
      rows: VisitRow[];
      total: number;
      page: number;
      pageSize: number;
    }>(res);
  },
  async getVisit(id: number) {
    const res = await fetch(`/api/admin/visits/${id}`);
    return handle<{ visit: VisitRow; events: VisitEvent[] }>(res);
  },
  async approve(
    id: number,
    body: { scheduled_date: string; scheduled_duration_min: number; message?: string },
  ) {
    const res = await fetch(`/api/admin/visits/${id}/approve`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    return handle<{ ok: boolean }>(res);
  },
  async deny(id: number, reason: string) {
    const res = await fetch(`/api/admin/visits/${id}/deny`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ reason }),
    });
    return handle<{ ok: boolean }>(res);
  },
  async editVisit(id: number, body: unknown) {
    const res = await fetch(`/api/admin/visits/${id}/edit`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    return handle<{ ok: boolean }>(res);
  },
  async archiveVisit(id: number, archived: boolean) {
    const res = await fetch(`/api/admin/visits/${id}/archive`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ archived }),
    });
    return handle<{ ok: boolean }>(res);
  },
  async publish(id: number, body: {
    is_public: boolean;
    event_title?: string;
    event_description?: string;
    event_image_url?: string;
  }) {
    const res = await fetch(`/api/admin/visits/${id}/publish`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    return handle<{ ok: boolean }>(res);
  },
  async listEvents(): Promise<{ events: PublicEvent[] }> {
    const res = await fetch(`/api/events`);
    return handle(res);
  },
  async listOrders(params: Record<string, string>) {
    const q = new URLSearchParams(params).toString();
    const res = await fetch(`/api/admin/orders?${q}`);
    return handle<{
      rows: OrderRow[];
      total: number;
      page: number;
      pageSize: number;
    }>(res);
  },
  async getOrder(id: number) {
    const res = await fetch(`/api/admin/orders/${id}`);
    return handle<{ order: OrderRow; events: OrderEvent[] }>(res);
  },
  async editOrder(id: number, body: unknown) {
    const res = await fetch(`/api/admin/orders/${id}/edit`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    return handle<{ ok: boolean }>(res);
  },
  async archiveOrder(id: number, archived: boolean) {
    const res = await fetch(`/api/admin/orders/${id}/archive`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ archived }),
    });
    return handle<{ ok: boolean }>(res);
  },
  async deleteOrder(id: number) {
    const res = await fetch(`/api/admin/orders/${id}/delete`, { method: "POST" });
    return handle<{ ok: boolean }>(res);
  },
  async submitOrder(body: unknown): Promise<{ ok: boolean; id: number; total_cents: number }> {
    const res = await fetch("/api/orders/submit", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    return handle(res);
  },
  async inquireAvailability(body: unknown): Promise<{ ok: boolean }> {
    const res = await fetch("/api/orders/inquire", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    return handle(res);
  },
};

export interface AdminUser {
  id: number;
  email: string;
  is_active: number;
  has_password: boolean;
  last_login_at: string | null;
  created_by: string | null;
  created_at: string;
}

export interface PublicEvent {
  id: number;
  scheduled_date: string;
  scheduled_duration_min: number | null;
  title: string;
  description: string | null;
  tour_type: string | null;
  image_url: string | null;
}

export interface VisitRow {
  id: number;
  status: "pending" | "approved" | "denied" | "cancelled";
  organization: string | null;
  contact_name: string;
  contact_email: string;
  contact_phone: string | null;
  address: string | null;
  attendees_count: number | null;
  attendees_breakdown: string | null;
  age_ranges: string | null;
  requested_dates: string | null;
  alternate_dates: string | null;
  tour_type: string | null;
  accessibility: string | null;
  purpose: string | null;
  photo_release: number;
  heard_from: string | null;
  ibew_affiliation: string | null;
  notes: string | null;
  scheduled_date: string | null;
  scheduled_duration_min: number | null;
  approved_by: string | null;
  approved_at: string | null;
  denied_reason: string | null;
  calendar_event_url: string | null;
  request_ip: string | null;
  user_agent: string | null;
  is_public: number;
  event_title: string | null;
  event_description: string | null;
  event_image_url: string | null;
  volunteer_interest: number;
  archived_at: string | null;
  archived_by: string | null;
  created_at: string;
  updated_at: string;
}

// A request is "expired" when it can no longer be acted on: an approved visit
// whose scheduled time has passed, or any other request whose latest requested
// / alternate date is in the past. Returns false if no dates are on file.
export function isVisitExpired(v: VisitRow): boolean {
  const now = Date.now();
  if (v.status === "approved" && v.scheduled_date) {
    const t = new Date(v.scheduled_date).getTime();
    return Number.isFinite(t) && t < now;
  }
  const slots: Array<{ date: string; time?: string }> = [];
  for (const raw of [v.requested_dates, v.alternate_dates]) {
    if (!raw) continue;
    try {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) slots.push(...arr);
    } catch {
      /* ignore */
    }
  }
  if (!slots.length) return false;
  const latest = Math.max(
    ...slots.map((s) => new Date(`${s.date}T${s.time || "23:59"}:00`).getTime()).filter(Number.isFinite),
  );
  return Number.isFinite(latest) && latest < now;
}

export interface VisitEvent {
  id: number;
  visit_id: number;
  actor_email: string | null;
  action: string;
  detail: string | null;
  created_at: string;
}

export type OrderStatus = "pending" | "acknowledged" | "paid" | "fulfilled" | "cancelled";

export interface OrderRow {
  id: number;
  status: OrderStatus;
  item_type: "brick" | "paver" | "bench" | "light_pole" | "ss_sign" | "medallion";
  with_logo: number;
  local_number: string | null;
  font: string | null;
  engraving_lines: string | null;
  design_description: string | null;
  plaque_name: string | null;
  quantity: number;
  unit_price_cents: number;
  total_cents: number;
  custom_logo_requested: number;
  purchaser_name: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  contact_phone: string;
  contact_email: string;
  ibew_affiliation: string | null;
  notes: string | null;
  archived_at: string | null;
  archived_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface OrderEvent {
  id: number;
  order_id: number;
  actor_email: string | null;
  action: string;
  detail: string | null;
  created_at: string;
}
